// 🔐 Restablecer contraseña — página PÚBLICA (sin sesión) que llega desde el
// enlace enviado por correo tras "¿Olvidaste tu contraseña?" en el Login.
// Ruta /reset-clave/:token (patrón /verificar-contrato/:codigo: fuera del
// árbol protegido). Estética coherente con el Login (escena oscura + acento
// del tema activo), mobile-first.
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { KeyRound, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { authAPI, loginSlugStore } from '@/lib/api/auth.api';
import { useTranslation } from '@/lib/hooks/useTranslation';
import { useThemeStore } from '@/stores/theme.store';
import { THEMES } from '@/lib/constants/themes';

function hue(triplet: string) {
  const [h = '200', s = '80%'] = String(triplet).split(/\s+/);
  return { h, s };
}

export function ResetClave() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentThemeId } = useThemeStore();
  const currentTheme = THEMES.find((th) => th.id === currentThemeId) || THEMES[0];
  const { h, s } = hue(currentTheme.colors.primary);
  const ACC = `hsl(${h} ${s} 62%)`;
  const ACCA = (a: number) => `hsl(${h} ${s} 62% / ${a})`;
  const SCENE = `radial-gradient(circle at 50% 18%, hsl(${h} 45% 12%) 0%, #060a10 55%, #03040a 100%)`;

  const [nueva, setNueva] = useState('');
  const [confirmar, setConfirmar] = useState('');

  const resetClave = useMutation({
    mutationFn: () => authAPI.resetClave(token || '', nueva),
    onSuccess: () => {
      toast.success(t('auth.resetPassword.successToast'));
      const slugRecordado = loginSlugStore.get();
      navigate(slugRecordado ? `/login/${slugRecordado}` : '/login', { replace: true });
    },
    // El error real (enlace inválido/expirado, clave muy corta) se muestra
    // inline (más abajo, con resetClave.error); nada de mensaje neutro aquí
    // porque el usuario YA está en el formulario con un token concreto.
  });

  const errNueva = nueva.length > 0 && nueva.length < 8 ? t('auth.resetPassword.minLengthError') : '';
  const errConf = confirmar.length > 0 && confirmar !== nueva ? t('auth.resetPassword.noMatchError') : '';
  const puede = !!token && nueva.length >= 8 && confirmar === nueva;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!puede || resetClave.isPending) return;
    resetClave.mutate();
  };

  const inputCls = 'w-full rounded-sm border bg-transparent px-2 py-1.5 text-sm text-[#e9f4fb] placeholder:text-white/20 focus:outline-none disabled:opacity-60';

  return (
    <div className="relative min-h-screen overflow-hidden font-mono" style={{ background: SCENE }}>
      <div className="pointer-events-none absolute inset-0" style={{ boxShadow: 'inset 0 0 240px 40px rgba(0,0,0,.85)' }} />

      <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, ease: 'easeOut' }} className="w-full max-w-md"
        >
          <div className="relative rounded-md border px-8 pb-7 pt-9 backdrop-blur-xl"
            style={{
              borderColor: ACCA(0.4),
              background: `linear-gradient(180deg, hsl(${h} 40% 9% / .55), rgba(4,7,12,.72))`,
              boxShadow: `0 0 90px ${ACCA(0.18)}, 0 30px 60px -20px rgba(0,0,0,.8), inset 0 0 60px ${ACCA(0.06)}`,
            }}>
            <div className="mb-6 flex flex-col items-center gap-2 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full"
                style={{ background: ACCA(0.16), border: `1px solid ${ACCA(0.4)}` }}>
                <KeyRound className="h-6 w-6" style={{ color: ACC }} />
              </div>
              <h1 className="text-[15px] font-semibold uppercase tracking-[0.2em] text-[#eaf4fb]">
                {t('auth.resetPassword.newTitle')}
              </h1>
              <p className="text-xs text-slate-400">{t('auth.resetPassword.newSubtitle')}</p>
            </div>

            {!token ? (
              <div className="space-y-3 text-center">
                <ShieldAlert className="mx-auto h-10 w-10 text-amber-300/90" />
                <p className="text-sm text-slate-300">{t('auth.resetPassword.invalidLink')}</p>
                <Button type="button" variant="outline" className="w-full" onClick={() => navigate('/login')}>
                  {t('auth.resetPassword.backToLogin')}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="nueva" className="mb-1 block text-[9px] uppercase tracking-[0.22em]" style={{ color: ACCA(0.7) }}>
                    {t('auth.resetPassword.newPasswordLabel')}
                  </label>
                  <input
                    id="nueva" type="password" value={nueva} onChange={(e) => setNueva(e.target.value)}
                    autoFocus autoComplete="new-password" disabled={resetClave.isPending}
                    className={inputCls} style={{ borderColor: ACCA(0.3) }}
                  />
                  {errNueva && <p className="mt-1 text-xs text-red-400">{errNueva}</p>}
                </div>
                <div>
                  <label htmlFor="confirmar" className="mb-1 block text-[9px] uppercase tracking-[0.22em]" style={{ color: ACCA(0.7) }}>
                    {t('auth.resetPassword.confirmPasswordLabel')}
                  </label>
                  <input
                    id="confirmar" type="password" value={confirmar} onChange={(e) => setConfirmar(e.target.value)}
                    autoComplete="new-password" disabled={resetClave.isPending}
                    className={inputCls} style={{ borderColor: ACCA(0.3) }}
                  />
                  {errConf && <p className="mt-1 text-xs text-red-400">{errConf}</p>}
                </div>

                {resetClave.isError && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded border border-red-500/40 bg-red-500/10 p-3">
                    <p className="text-sm text-red-300">{getErrorMessage(resetClave.error)}</p>
                    <button type="button" className="mt-1 text-xs text-slate-400 underline-offset-2 hover:text-slate-200 hover:underline"
                      onClick={() => navigate('/login')}>
                      {t('auth.resetPassword.requestAgain')}
                    </button>
                  </motion.div>
                )}

                <button type="submit" disabled={!puede || resetClave.isPending}
                  className="relative mt-2 flex w-full items-center justify-center gap-2 overflow-hidden rounded-sm py-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#04121a] transition hover:brightness-110 disabled:opacity-70"
                  style={{ background: `linear-gradient(180deg, hsl(${h} ${s} 58%), hsl(${h} ${s} 44%))`, boxShadow: `0 0 26px ${ACCA(0.55)}, inset 0 0 12px rgba(255,255,255,.22)` }}>
                  {resetClave.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {resetClave.isPending ? t('auth.resetPassword.submittingNew') : t('auth.resetPassword.submitNew')}
                </button>

                <div className="text-center">
                  <button type="button" className="text-xs text-slate-400 transition-colors hover:text-slate-200"
                    onClick={() => navigate('/login')}>
                    {t('auth.resetPassword.backToLogin')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
